import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { syncSpotifyTokens, getEnrichmentStatus, claimAnonymous } from '../utils/backendApi';
import { saveApplaussAuth, peekDeviceId } from '../utils/authManager';
import { fetchRecommendations } from '../utils/oauthFetchRecommendations';
import { recommendationsPathFromNavData } from '../utils/recommendationFilters';
import UserLoading from './UserLoading';
import AuthErrorBanner, { showAuthError } from './AuthErrorBanner';

// How long handleEnrichmentDone waits on the recommendations prefetch before
// giving up and letting HomeRedirect do the work.
const PREFETCH_WAIT_MS = 8000;

/**
 * Component to handle OAuth callbacks globally
 * Detects OAuth tokens in URL and processes them before any route navigation
 */
const OAuthHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // isProcessing gates the URL-param useEffect so a second render can't re-trigger
  // the same OAuth flow. It no longer drives any UI — showEnrichmentLoading handles that.
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEnrichmentLoading, setShowEnrichmentLoading] = useState(false);
  const [enrichmentArtistCount, setEnrichmentArtistCount] = useState(0);
  const [enrichmentUserId, setEnrichmentUserId] = useState(null);

  const [isEnrichmentReady, setIsEnrichmentReady] = useState(false);
  const pendingNavData = useRef(null);
  const enrichmentPollRef = useRef(null);

  // Poll enrichment status from here so we don't have stale-closure problems inside the loading component
  useEffect(() => {
    if (!enrichmentUserId) return;

    enrichmentPollRef.current = setInterval(async () => {
      try {
        const { status } = await getEnrichmentStatus();
        if (status === 'done' || status === 'error' || status === 'not_found') {
          clearInterval(enrichmentPollRef.current);
          setIsEnrichmentReady(true);
        }
      } catch {
        clearInterval(enrichmentPollRef.current);
        setIsEnrichmentReady(true);
      }
    }, 2000);

    return () => clearInterval(enrichmentPollRef.current);
  }, [enrichmentUserId]);

  // Prefetch the first recommendations only once the profile is actually usable:
  // the access token is stored (Spotify stores it when syncSpotifyTokens resolves)
  // and background enrichment has settled. Firing this at the start of the flow
  // sent an anonymous / pre-enrichment request whose result the recommendations
  // page then rendered verbatim — issue #450.
  useEffect(() => {
    if (!isEnrichmentReady) return;
    pendingNavData.current = fetchRecommendations();
  }, [isEnrichmentReady]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const expiresIn = params.get('expires_in');
    const userEmail = params.get('user_email');
    const userId = params.get('user_id');
    const userDisplayName = params.get('user_display_name');
    const userPicture = params.get('user_picture');
    const provider = params.get('provider');
    const providersParam = params.get('providers');
    const error = params.get('error');

    // If there's an error, show it and clean up URL. Never the provider's own `details` (#683).
    if (error) {
      console.error('OAuth auth error:', error, params.get('details'));
      showAuthError();
      navigate(window.location.pathname, { replace: true }); // Clean URL
      return;
    }

    // If we have OAuth tokens, process them
    if (accessToken && refreshToken && !isProcessing) {
      setIsProcessing(true);
      console.log('🔐 Processing OAuth callback...');

      // Check if this is Google OAuth (has user info) or Spotify (needs sync)
      if (userEmail && userId) {
        // Google OAuth - Save directly
        console.log('✅ Google OAuth successful:', userEmail);

        const artistCount = parseInt(params.get('artist_count')) || 0;

        // Parse providers array if available
        let providers = [provider || 'google'];
        if (providersParam) {
          try {
            providers = JSON.parse(providersParam);
          } catch (e) {
            console.error('Failed to parse providers:', e);
          }
        }

        // Capture device_id BEFORE saveApplaussAuth rotates it.
        const preLoginDeviceId = peekDeviceId();

        saveApplaussAuth({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: parseInt(expiresIn) || 3600,
          user: {
            email: userEmail,
            id: userId,
            display_name: userDisplayName,
            picture: userPicture,
            provider: provider || 'google',
            providers: providers
          }
        }, provider || 'google');

        // OAuth redirects can't carry device_id through the state param, so
        // merge any pre-login anonymous activity here. Non-fatal: a failed
        // merge logs but never blocks the user landing on the app.
        if (preLoginDeviceId) {
          claimAnonymous(preLoginDeviceId).catch(err => {
            console.warn('claim-anonymous failed (non-fatal):', err);
          });
        }

        // Always show UserLoading — with artistCount=0 it completes after the minimum display time
        setEnrichmentArtistCount(artistCount);
        setShowEnrichmentLoading(true);
        if (artistCount > 0) {
          setEnrichmentUserId(userId);
          setIsEnrichmentReady(false);
        } else {
          setIsEnrichmentReady(true);
        }
      } else {
        // Spotify OAuth - show loading screen immediately (sync itself takes time)
        setEnrichmentArtistCount(0);
        setIsEnrichmentReady(false);
        setShowEnrichmentLoading(true);

        // Capture device_id BEFORE syncSpotifyTokens calls saveApplaussAuth.
        const preLoginDeviceId = peekDeviceId();

        syncSpotifyTokens({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: parseInt(expiresIn)
        }, 20)
          .then(response => {
            console.log('✅ Spotify login successful! User:', response.user.email);
            // Auth is already saved by syncSpotifyTokens()

            if (preLoginDeviceId) {
              claimAnonymous(preLoginDeviceId).catch(err => {
                console.warn('claim-anonymous failed (non-fatal):', err);
              });
            }

            const artistCount = response.artist_count || 0;
            if (artistCount > 0) {
              // Background classification running — update count and start polling
              setEnrichmentArtistCount(artistCount);
              setEnrichmentUserId(response.user.id);
            } else {
              // Sync done, no background work — complete the loading screen now
              setIsEnrichmentReady(true);
            }
          })
          .catch(error => {
            console.error('❌ Failed to authenticate with Applauss:', error);
            setShowEnrichmentLoading(false);
            setIsProcessing(false);
            showAuthError();
            navigate('/', { replace: true });
          });
      }
    }
  }, [location.search, navigate, isProcessing]);

  // Called by UserLoading when the animation completes.
  const handleEnrichmentDone = async () => {
    // Await before tearing the loading screen down, so a prefetch that is still
    // in flight doesn't flash the app underneath. The axios instance has no
    // timeout, so cap the wait — on expiry we fall through to '/', which is
    // what a not-yet-resolved prefetch did before, and HomeRedirect sends an
    // authenticated user on to the recommendations page from there.
    const navData = await Promise.race([
      pendingNavData.current,
      new Promise(resolve => setTimeout(() => resolve(null), PREFETCH_WAIT_MS))
    ]);
    // Navigate BEFORE clearing isProcessing: that flag is what stops the
    // URL effect from re-entering the flow, and the OAuth params are still in
    // location.search until this navigation replaces them.
    if (navData) {
      navigate(recommendationsPathFromNavData(navData), { replace: true, state: navData });
    } else {
      navigate('/', { replace: true });
    }
    setShowEnrichmentLoading(false);
    setIsProcessing(false);
  };

  // UserLoading screen for every OAuth login flow, else the app routes. The banner sits beside
  // either, so a failure reported while one swaps for the other is still shown.
  return (
    <>
      <AuthErrorBanner />
      {showEnrichmentLoading ? (
        <UserLoading
          artistCount={enrichmentArtistCount}
          isReady={isEnrichmentReady}
          onDone={handleEnrichmentDone}
          logoAnimated
        />
      ) : (
        children
      )}
    </>
  );
};

export default OAuthHandler;
